import { ChevronLeft, ChevronRight, Loader2, Users } from 'lucide-react'
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  GROUPS_PAGE_SIZE,
  useDismissGroup,
  useEntityGroups,
  useGroupPendingCount,
  useMergeGroup,
  useRejectGroupMember,
} from '@/features/entity-resolution/hooks/useGroupReview'
import { exactGroupEligible } from '@/features/entity-resolution/identityAssessment'
import type { GroupMember, ResolutionGroup } from '@/services/api/entity-resolution-groups'
import { cn } from '@/utils/cn'
import { ClusterRow } from './ClusterRow'
import { HeadToHeadPanel } from './HeadToHeadPanel'

interface GroupReviewPanelProps {
  caseId: string
  /** Page actions rendered on the header row, right of "Resolution queue".
   * Living here (not above the panel) keeps them visible in every state —
   * including the empty one, where "Find more duplicates" is the call to action. */
  actions?: ReactNode
}

interface Selection {
  groupId: string
  memberId: string
}

/** The richest member (most relationships) is the default canonical — least info
 * lost. Fallback for legacy/property groups; name groups carry a backend-chosen
 * `representativeId` (the same node, plus a stable tie-break) which wins. */
function defaultCanonical(members: GroupMember[]): string | undefined {
  return [...members].sort((a, b) => (b.degree ?? 0) - (a.degree ?? 0))[0]?.id
}

/**
 * Below lg the panel is one scroll and the head-to-head takes the queue's place:
 * opening it starts at the top, closing it returns to where the queue was left.
 * From lg up the panel itself never scrolls, so this does nothing there.
 */
function usePaneScroll(showDetail: boolean) {
  const ref = useRef<HTMLDivElement>(null)
  const queueTop = useRef(0)
  useLayoutEffect(() => {
    if (ref.current) ref.current.scrollTop = showDetail ? 0 : queueTop.current
  }, [showDetail])
  const onScroll = () => {
    if (!showDetail && ref.current) queueTop.current = ref.current.scrollTop
  }
  return { ref, onScroll }
}

/**
 * Two-pane duplicate review: a queue of clusters on the left (each expands to a
 * grid of candidate cards), and a head-to-head of the selected candidate against
 * the cluster's canonical on the right.
 */
export function GroupReviewPanel({ caseId, actions }: GroupReviewPanelProps) {
  const [page, setPage] = useState(0)
  const { data, isLoading, isError, isPlaceholderData } = useEntityGroups(caseId, {
    limit: GROUPS_PAGE_SIZE,
    offset: page * GROUPS_PAGE_SIZE,
  })
  const groups = data?.groups ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / GROUPS_PAGE_SIZE))
  // Header counts are case-wide (the page only sees up to GROUPS_PAGE_SIZE groups).
  const { data: pending } = useGroupPendingCount(caseId)
  const merge = useMergeGroup(caseId)
  const reject = useRejectGroupMember(caseId)
  const dismiss = useDismissGroup(caseId)
  const busy = merge.isPending || reject.isPending || dismiss.isPending

  // Merging away the last cluster of the final page steps the pager back
  // instead of stranding the analyst on an empty page.
  useEffect(() => {
    if (page >= pageCount) setPage(Math.max(0, pageCount - 1))
  }, [page, pageCount])

  const [overrides, setOverrides] = useState<Record<string, string>>({})
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [selected, setSelected] = useState<Selection | null>(null)

  const didInit = useRef(false)
  useEffect(() => {
    if (!didInit.current && groups.length > 0) {
      didInit.current = true
      setExpanded(new Set([groups[0].groupId]))
    }
  }, [groups])

  const canonicalOf = (g: ResolutionGroup) =>
    overrides[g.groupId] ?? g.representativeId ?? defaultCanonical(g.members)

  const toggleExpand = (groupId: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })

  const mergeMembers = (g: ResolutionGroup, memberIds: string[]) => {
    const keep = canonicalOf(g)
    if (!keep || memberIds.length === 0) return
    merge.mutate({ group_id: g.groupId, survivor_id: keep, member_ids: memberIds })
    setSelected(null)
  }

  const removeCandidate = (groupId: string, memberId: string) => {
    reject.mutate({ group_id: groupId, member_id: memberId })
    setSelected(prev => (prev?.memberId === memberId ? null : prev))
  }

  const dismissGroup = (groupId: string) => {
    dismiss.mutate({ group_id: groupId })
    // Anything selected inside the dismissed cluster no longer exists.
    setSelected(prev => (prev?.groupId === groupId ? null : prev))
  }

  const setCanonical = (groupId: string, memberId: string) => {
    setOverrides(prev => ({ ...prev, [groupId]: memberId }))
    // The newly-canonical entity can't be compared with itself — drop a detail on it.
    setSelected(prev => (prev?.groupId === groupId && prev.memberId === memberId ? null : prev))
  }

  const selGroup = selected ? (groups.find(g => g.groupId === selected.groupId) ?? null) : null
  const selSurvivor = selGroup
    ? (selGroup.members.find(m => m.id === canonicalOf(selGroup)) ?? null)
    : null
  const selMember = selGroup
    ? (selGroup.members.find(m => m.id === selected?.memberId) ?? null)
    : null
  const showDetail = Boolean(selSurvivor && selMember && selMember.id !== selSurvivor.id)
  const paneScroll = usePaneScroll(showDetail)
  // Backend-stored similarity of the selected member to the representative — shown
  // as-is in the head-to-head (the UI never recomputes its own score).
  const selSim = selMember?.similarityScore ?? 0

  // Case-wide counts for the header; the page-derived numbers are the fallback
  // until the pending-count query lands. One member per group survives, so
  // candidates-to-clear = members − groups.
  const clusterCount = pending?.groups ?? total
  const candidates = pending
    ? Math.max(0, pending.candidates - pending.groups)
    : groups.reduce((sum, g) => sum + Math.max(0, g.members.length - 1), 0)
  // Exact-duplicate clusters first (clear them fast), then the biggest. With
  // pagination this ordering applies within the current page only; the backend
  // orders pages by member count, so pages are stable across refetches.
  const sortedGroups = [...groups].sort(
    (a, b) =>
      Number(exactGroupEligible(b)) - Number(exactGroupEligible(a)) ||
      b.members.length - a.members.length
  )
  const pageStart = page * GROUPS_PAGE_SIZE

  // The header (and the page actions on it) stays mounted in every state —
  // "Find more duplicates" is the call to action precisely when the queue is
  // empty. Only the body below swaps.
  let body: ReactNode
  if (isLoading) {
    body = (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading possible duplicates…
      </div>
    )
  } else if (isError) {
    body = (
      <div className="py-16 text-center text-destructive">Could not load possible duplicates.</div>
    )
  } else if (total === 0) {
    // `total` (not `groups.length`): a stale placeholder page can be empty
    // while earlier pages still hold clusters.
    body = (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-muted-foreground">
        <Users className="size-6" />
        <p>No possible duplicates to review.</p>
        <p className="text-sm">Run “Find more duplicates”, or ingest more data.</p>
      </div>
    )
  } else {
    body = (
      // Below lg the panel scrolls as one (see the wrapper), so the panes keep
      // their natural height there instead of scrolling inside.
      <div className="flex flex-1 gap-4 lg:min-h-0 lg:overflow-hidden">
        {/* Below lg the two panes do not fit side by side: the head-to-head takes
            the queue's place, and closing it brings the queue back. */}
        <div
          className={cn('min-h-0 flex-col', showDetail ? 'hidden w-1/2 lg:flex' : 'flex w-full')}
        >
          <div
            className={cn(
              'flex-1 space-y-3 overflow-y-auto lg:pr-1',
              isPlaceholderData && 'opacity-60'
            )}
          >
            {sortedGroups.map(group => (
              <ClusterRow
                key={group.groupId}
                group={group}
                busy={busy}
                view={{
                  canonicalId: canonicalOf(group),
                  selectedMemberId: selected?.groupId === group.groupId ? selected.memberId : null,
                  expanded: expanded.has(group.groupId),
                }}
                on={{
                  toggleExpand: () => toggleExpand(group.groupId),
                  selectCandidate: id => setSelected({ groupId: group.groupId, memberId: id }),
                  setCanonical: id => setCanonical(group.groupId, id),
                  removeCandidate: id => removeCandidate(group.groupId, id),
                  mergeAll: () =>
                    mergeMembers(
                      group,
                      group.members.filter(m => m.id !== canonicalOf(group)).map(m => m.id)
                    ),
                  dismissAll: () => dismissGroup(group.groupId),
                }}
              />
            ))}
          </div>

          {pageCount > 1 && (
            <div className="flex shrink-0 items-center justify-between border-t border-border/60 px-1 pt-2.5">
              <span className="text-2xs text-muted-foreground">
                {`${pageStart + 1}–${Math.min(pageStart + GROUPS_PAGE_SIZE, total)} of ${total}`}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  size="icon-xs"
                  variant="ghost"
                  className="max-md:size-9"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft />
                </Button>
                <span className="text-2xs tabular-nums text-muted-foreground">
                  {page + 1} / {pageCount}
                </span>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  className="max-md:size-9"
                  disabled={page >= pageCount - 1}
                  onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                  aria-label="Next page"
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
          )}
        </div>

        {showDetail && selGroup && selSurvivor && selMember && (
          <HeadToHeadPanel
            comparison={{
              survivor: selSurvivor,
              member: selMember,
              similarity: selSim,
              assessment: selGroup.identityAssessment,
            }}
            busy={busy}
            on={{
              merge: () => mergeMembers(selGroup, [selMember.id]),
              reject: () => removeCandidate(selGroup.groupId, selMember.id),
              close: () => setSelected(null),
            }}
          />
        )}
      </div>
    )
  }

  return (
    // flex-1 (not h-full): the panel fills the remaining space in PageShell's
    // flex column.
    <div
      ref={paneScroll.ref}
      onScroll={paneScroll.onScroll}
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 md:px-6 md:pb-6 lg:overflow-hidden"
    >
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 pb-3 lg:flex-nowrap">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Resolution queue</h2>
          {(pending || data) && (
            <p className="text-xs text-muted-foreground">
              {clusterCount} cluster{clusterCount === 1 ? '' : 's'} · {candidates} candidate
              {candidates === 1 ? '' : 's'} to clear
            </p>
          )}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:flex-nowrap">
            {actions}
          </div>
        )}
      </div>
      {body}
    </div>
  )
}
